import React, { useEffect, useState } from 'react'
import { Controller, FieldError, useFieldArray, UseFormReturn } from 'react-hook-form'
import {
  CreateProductCatalogDto,
  ProductCatalogDto,
  ProductPackageUom,
  SupplierDto,
  SupplierType,
} from '@services/dtos'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Button, FormHelperText, Grid, IconButton, List, ListItem, ListItemText, Tooltip } from '@mui/material'
import InputField from '@components/inputs/InputField'
import { getSuppliers, setSupplierFilters } from '@state/reducers/supplierReducer'
import { AddWrapperInput } from '@components/inputs/AddWrapperInput'
import SupplierForm from '../../SupplierForm'
import ServerAutocomplete from '@components/autocomplete/ServerAutocomplete'
import SelectField from '@components/inputs/SelectField'
import { Close as CloseIcon } from '@mui/icons-material'
import { findAll as findAllExam } from '@state/thunks/procedureThunk'
import { useSelector } from '@state/store'
import { CheckBoxInput } from '@components/inputs'
import ExamSelectorDialog from '@components/dialogs/ExamSelectorDialog'
import { MemberDto, MemberRoleEnum, MemberService } from '@services/api'
import { MemberRoleEnumLabels } from '@utils/constants'
import ServerAutocompleteMultiple from '@components/autocomplete/ServerAutocompleteMultiple'
import { ProductCatalogPrices } from '../components/ProductCatalogPrices'

const MEMBER_ROLE_OPTIONS =
  (Object.entries(MemberRoleEnumLabels) as Array<[MemberRoleEnum, string]>)
    .map(([value, label]) => ({ label, value }))

type Props = {
  useCreateForm: UseFormReturn<CreateProductCatalogDto, object>
  editingProductCatalog?: ProductCatalogDto | null
}

const ProductCatalogSectionOrder: React.FC<Props> = ({ useCreateForm: createForm, editingProductCatalog }) => {
  const { control, watch, setValue, formState: { errors } } = createForm
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'validation.rules',
  })
  const { fields: priceFields, append: appendPrice, remove: removePrice, update: updatePrice } = useFieldArray({
    control,
    name: 'prices',
  })

  const [isExamSelectorVisible, setIsExamSelectorVisible] = useState(false)

  const rules = watch('validation.rules')

  const { exams } = useSelector(({ procedure }) => ({
    exams: procedure.exams.datas,
  }))

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(findAllExam({}))
    return () => {
      dispatch(setSupplierFilters({ search: '', type: undefined }))
    }
  }, [dispatch])

  useEffect(() => {
    if (editingProductCatalog?.validation?.rules) {
      for (const member of editingProductCatalog.validation.rules.flatMap(r => r.members)) {
        itemCacheRef.current.set(member.id, member)
      }
    }
  }, [editingProductCatalog])

  const fetchSuppliers = async (search: string) => {
    dispatch(setSupplierFilters({ search, type: SupplierType.PRODUCT_SUPPLIER }))
    const page = await dispatch(getSuppliers()).unwrap()
    return page?.datas ?? []
  }

  const isPack = watch('packageUom') === ProductPackageUom.PACK

  const formFields = [
    { name: 'supplyLeadTimeDays', label: 'Délai approvisionnement (j)', type: 'number', md: 4 },
    { name: 'gtinEan', label: 'GTIN / EAN', md: 8 },
  ]

  const removeExam = (removedExamId: number) => {
    const isConfirmed = window.confirm(
      'Voulez-vous vraiment supprimer cet examen ?',
    )
    if (!isConfirmed) {
      return
    }

    setValue(
      'examIds',
      (watch('examIds') || []).filter((examId) => examId !== removedExamId),
    )
  }

  const itemCacheRef = React.useRef(new Map<number, MemberDto>())

  const getMembersByRole = async (role: string, search: string) => {
    const response = await MemberService.findAll({ role, limit: 100, search })
    const list = response.datas ?? []
    for (const it of list) {
      itemCacheRef.current.set(it.id, it)
    }
    return list
  }

  return (
    <>
      {formFields.map((field) => (
        <Grid item xs={12} md={field.md} key={field.name}>
          <InputField
            control={control}
            name={field.name}
            label={field.label}
          />
        </Grid>
      ))}

      <Grid item xs={12} md={isPack ? 9 : 12}>
        <SelectField
          control={control}
          name={'packageUom'}
          label={'Unité de conditionnement'}
          options={[
            { label: 'Unité / Dose', value: ProductPackageUom.UNIT },
            { label: 'Lot', value: ProductPackageUom.PACK },
          ]}
          data-cy="select-package-uom"
        />
      </Grid>

      {isPack && (
        <Grid item xs={12} md={3}>
          <InputField
            control={control}
            name={'unitsPerPack'}
            label={'Unités / doses par lot'}
            type={'number'}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <ProductCatalogPrices 
          prices={priceFields} 
          appendPrice={appendPrice} 
          removePrice={removePrice} 
          updatePrice={updatePrice}
          error={(errors.prices as unknown as FieldError)?.message}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <AddWrapperInput
          legend="Fournisseur"
          addTooltipTitle="Ajouter un fournisseur"
          dialogContent={(close) => (
            <SupplierForm onClose={close} defaultValues={{ type: SupplierType.PRODUCT_SUPPLIER }} />
          )}
          onAfterClose={() => {
            dispatch(getSuppliers())
          }}
          input={(<ServerAutocomplete<SupplierDto>
            fetchService={fetchSuppliers}
            onChange={(value: SupplierDto | null) => {
              setValue('supplierId', value?.id ?? '')
            }}
            getValue={(value) => value?.id?.toString() || null}
            getOptionLabel={(value) => {
              const supplier = value as SupplierDto
              return supplier ? supplier.name : ''
            }}
            debounceMs={300}
            placeholder="Rechercher un fournisseur"
            label="Fournisseur"
            helperText={errors.supplierId?.message}
            error={!!errors.supplierId}
            minSearchLength={0}
            initialOptions={editingProductCatalog?.supplier ? [editingProductCatalog.supplier] : []}
            value={editingProductCatalog?.supplier ?? null}
          />)}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <fieldset className="border border-gray-200 p-4 rounded-lg">
          <legend className="text-sm font-medium text-gray-700 mb-1 border border-gray-200 p-2 rounded-lg">
            Examens
          </legend>
          <div className="w-full mb-4">
            <CheckBoxInput
              control={control}
              name="orderableIfExamAssociated"
              label="Commande possible si examen futur uniquement" />
          </div>
          <Button
            variant="contained"
            onClick={() => setIsExamSelectorVisible(true)}
            size="small"
            sx={{ border: errors.examIds ? '1px solid #d3302f' : 'none' }}
          >
            Ajouter un exam
          </Button>
          {errors.examIds && (
            <FormHelperText error>
              {(errors.examIds as unknown as FieldError).message}
            </FormHelperText>
          )}

          {(watch('examIds') ?? []).length > 0 && (
            <List
              className="border border-1"
              style={{ padding: 0, marginTop: 16 }}
            >
              {exams
                .filter(({ id }) =>
                  (watch('examIds') ?? []).includes(id),
                )
                .map((selectedExam) => (
                  <ListItem
                    key={selectedExam.id}
                    button
                    className={'hover:bg-gray-100'}
                    secondaryAction={
                      <IconButton
                        onClick={() => removeExam(selectedExam.id)}
                        edge="end"
                        aria-label="delete"
                      >
                        <CloseIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      style={{ fontSize: '10px' }}
                      primary={`(${selectedExam.code}) ${selectedExam.label}`}
                    />
                  </ListItem>
                ))}
            </List>
          )}
        </fieldset>
      </Grid>

      <Grid item xs={12}>
        <fieldset className="border border-gray-200 p-4 rounded-lg">
          <legend className="text-sm font-medium text-gray-700 mb-1 border border-gray-200 p-2 rounded-lg">
            Validations nécessaires
          </legend>

          {rules?.length > 1 && (
            <SelectField
              label="Relation entre les groupes"
              options={[
                { value: 'AND', label: 'Tous les groupes nécessaires (ET)' },
                { value: 'OR', label: 'Au moins un groupe nécessaire (OU)' },
              ]}
              control={control}
              name={'validation.groupOperator'}
              data-cy="select-group-operator"
            />
          )}

          {fields.map((field, index) => {
            const role = watch(`validation.rules.${index}.role`)

            return (
              <fieldset key={field.id} className="border border-gray-200 p-4 rounded-lg mt-4">
                <legend className="text-sm font-medium text-gray-700 mb-1 border border-gray-200 p-2 rounded-lg">
                  <span>Groupe {index + 1}</span>
                  <span className="inline-flex items-center">
                    <Tooltip title={'Supprimer'} arrow>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          remove(index)
                          const newLength = rules.length - 1
                          if (newLength <= 1) {
                            setValue('validation.groupOperator', undefined)
                          }
                        }}
                        sx={{
                          paddingTop: 0.5,
                          paddingBottom: 0.5,
                          paddingLeft: 1,
                          paddingRight: 1,
                          minWidth: 'auto',
                          width: 'auto',
                          marginLeft: 1,
                        }}
                      >
                        <i className="fas fa-trash" />
                      </Button>
                    </Tooltip>
                  </span>
                </legend>

                <Grid container item spacing={2} xs={12}>
                  <Grid item xs={12} sm={6}>
                    <SelectField
                      label="Rôle"
                      options={MEMBER_ROLE_OPTIONS}
                      control={control}
                      name={`validation.rules.${index}.role`}
                      data-cy="select-role"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SelectField
                      label="Condition entre les membres"
                      options={[
                        { value: 'AND', label: 'ET (tous doivent valider)' },
                        { value: 'OR', label: 'OU (au moins un)' },
                      ]}
                      control={control}
                      name={`validation.rules.${index}.operator`}
                    />
                  </Grid>

                  {role && <Grid item xs={12}>
                    <Controller
                      name={`validation.rules.${index}.memberIds`}
                      control={control}
                      render={({ field: { value: ids = [], onChange } }) => {
                        const selected: MemberDto[] = ids
                          .map((id) => itemCacheRef.current.get(+id))
                          .filter(Boolean) as MemberDto[]
                        const error = errors?.validation?.rules?.[index]?.memberIds

                        return (
                          <ServerAutocompleteMultiple<MemberDto>
                            values={selected}
                            onChange={(members) => onChange(members.map(({ id }) => id))}
                            fetchService={(search) => getMembersByRole(role, search)}
                            getOptionLabel={({ firstName, lastName }) => `${firstName} ${lastName}`}
                            label="Membres"
                            minSearchLength={1}
                            placeholder={`Rechercher des '${MemberRoleEnumLabels[role]}'...`}
                            helperText={(error as unknown as FieldError)?.message}
                            error={!!error}
                          />
                        )
                      }}
                    />
                  </Grid>}
                </Grid>
              </fieldset>
            )
          })}

          <Grid item xs={12} mt={2}>
            <Button
              variant="contained"
              size="small"
              onClick={() => append({ role: '', operator: 'AND', memberIds: [] })}
            >
              Ajouter un groupe
            </Button>
          </Grid>
        </fieldset>
      </Grid>

      <ExamSelectorDialog
        isOpen={isExamSelectorVisible}
        onClose={() => setIsExamSelectorVisible(false)}
        handleConfirm={(examIds: number[]) => setValue('examIds', examIds)}
        excludedExams={[]}
      />
    </>
  )
}

export default ProductCatalogSectionOrder
